import { useState, forwardRef, useMemo } from "react";
import type { LLMModel } from "@pick-three-ai/constants";
import type { RankingState } from "../hooks";
import { useAppMode } from "../hooks";
import { RankingSlot } from "./RankingSlot";
import { ModelPicker } from "./ModelPicker";
import { analytics } from "../lib/analytics";
import type { CardTransform } from "./TestControlDialog";

type RankPosition = 1 | 2 | 3;

interface RankingCardProps {
  rankings: RankingState;
  onModelChange: (position: 1 | 2 | 3, model: string | null) => void;
  onBlurbChange: (position: 1 | 2 | 3, blurb: string) => void;
  title?: string;
  onTitleChange?: (title: string) => void;
  handle?: string;
  onHandleChange?: (handle: string) => void;
  isReadOnly?: boolean;
  transform?: CardTransform;
}

export const RankingCard = forwardRef<HTMLDivElement, RankingCardProps>(function RankingCard({
  rankings,
  onModelChange,
  onBlurbChange,
  title = "",
  onTitleChange,
  handle = "",
  onHandleChange,
  isReadOnly = false,
  transform,
}, ref) {
  const [activeSlot, setActiveSlot] = useState<RankPosition | null>(null);
  const { isGeneric } = useAppMode();
  
  const formattedDate = new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" });

  // Compute 3D transform styles
  const transformStyle = useMemo(() => {
    if (!transform) return undefined;
    return {
      perspective: `${transform.perspective}px`,
    };
  }, [transform]);

  const cardStyle = useMemo(() => {
    if (!transform) return undefined;
    return {
      transform: `rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg) rotateZ(${transform.rotateZ}deg) scale(${transform.scale})`,
      transformStyle: "preserve-3d" as const,
      transition: "transform 0.15s ease-out",
    };
  }, [transform]);

  const handleSlotClick = (rank: RankPosition) => {
    if (isReadOnly) return;
    const isOpening = activeSlot !== rank;
    if (isOpening) {
      analytics.rankSlotOpened(rank);
    }
    setActiveSlot(activeSlot === rank ? null : rank);
  };

  const handleModelSelect = (model: LLMModel) => {
    if (activeSlot === null) return;
    analytics.modelSelected(activeSlot, model);
    onModelChange(activeSlot, model);
    setActiveSlot(null);
  };

  const handleTitleBlur = () => {
    if (title.trim()) {
      analytics.titleEntered(title.trim().length);
    }
  };

  const handleHandleBlur = () => {
    if (handle.trim()) {
      analytics.handleEntered(handle.trim());
    }
  };

  const handlePickerClose = () => {
    setActiveSlot(null);
  };

  return (
    <>
      <div className="w-full max-w-md mx-auto" ref={ref} style={transformStyle}>
        {/* Main Card */}
        <div className="glass-strong rounded-2xl sm:rounded-3xl p-3 sm:p-4 flex flex-col" style={cardStyle}>
          {/* Header with date, title, and handle - use grid for true centering */}
          <div className="mb-3 pb-2 border-b border-white/10 grid grid-cols-[auto_1fr_auto] items-center gap-2">
            {/* Date - left */}
            <span className="text-xs sm:text-sm text-muted-foreground/70 font-medium">
              {formattedDate}
            </span>
            
            {/* Title - center */}
            {isReadOnly ? (
              <span className="text-center text-base sm:text-lg font-bold text-foreground truncate px-2">
                {title || "Best Overall"}
              </span>
            ) : (
              <input
                type="text"
                value={title}
                onChange={(e) => onTitleChange?.(e.target.value)}
                onBlur={handleTitleBlur}
                placeholder="Best Overall..."
                maxLength={30}
                className="text-center bg-transparent border-none outline-none text-base sm:text-lg font-bold text-foreground placeholder:text-muted-foreground/50 focus:placeholder:text-muted-foreground/30"
              />
            )}
            
            {/* Handle - right (invisible spacer in read-only mode when no handle) */}
            {isReadOnly && !handle ? (
              <span className="invisible text-xs sm:text-sm">{formattedDate}</span>
            ) : (
              <div 
                data-handle-container="true"
                className="flex items-center text-xs sm:text-sm text-muted-foreground/70 font-medium"
              >
                <span>By @</span>
                {isReadOnly ? (
                  <span className="text-muted-foreground/70 font-medium">{handle}</span>
                ) : (
                  <input
                    type="text"
                    value={handle}
                    onChange={(e) => onHandleChange?.(e.target.value)}
                    onBlur={handleHandleBlur}
                    placeholder="fam"
                    maxLength={3}
                    className="w-[4ch] min-w-[3ch] bg-transparent border-none outline-none text-base sm:text-xs text-muted-foreground/70 font-medium placeholder:text-muted-foreground/40 focus:placeholder:text-muted-foreground/25"
                    style={{ width: `${Math.max(3, handle.length || 3)}ch` }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Ranking Slots */}
          <div className="flex flex-col">
            {/* 1st Place - Hero slot with halo */}
            <div className="halo-gold mb-3 sm:mb-4">
              <RankingSlot
                rank={1}
                model={rankings[0].model}
                blurb={rankings[0].blurb}
                onModelClick={() => handleSlotClick(1)}
                onModelTextChange={(text) => onModelChange(1, text || null)}
                onBlurbChange={(blurb) => onBlurbChange(1, blurb)}
                isActive={activeSlot === 1}
                isReadOnly={isReadOnly}
                isGeneric={isGeneric}
              />
            </div>

            {/* 2nd & 3rd Place - slightly narrower */}
            <div className="space-y-1.5 sm:space-y-2 mx-2 sm:mx-3">
              <div className="halo-silver">
                <RankingSlot
                  rank={2}
                  model={rankings[1].model}
                  blurb={rankings[1].blurb}
                  onModelClick={() => handleSlotClick(2)}
                  onModelTextChange={(text) => onModelChange(2, text || null)}
                  onBlurbChange={(blurb) => onBlurbChange(2, blurb)}
                  isActive={activeSlot === 2}
                  isReadOnly={isReadOnly}
                  isGeneric={isGeneric}
                />
              </div>
              <div className="halo-bronze">
                <RankingSlot
                  rank={3}
                  model={rankings[2].model}
                  blurb={rankings[2].blurb}
                  onModelClick={() => handleSlotClick(3)}
                  onModelTextChange={(text) => onModelChange(3, text || null)}
                  onBlurbChange={(blurb) => onBlurbChange(3, blurb)}
                  isActive={activeSlot === 3}
                  isReadOnly={isReadOnly}
                  isGeneric={isGeneric}
                />
              </div>
            </div>
          </div>

          {/* Footer - visible in app (editor mode) */}
          {!isReadOnly && (
            <div 
              data-hide-on-export="true"
              className="mt-3 pt-2.5 border-t border-white/10 flex items-center justify-center"
            >
              <span className="text-sm text-muted-foreground/80 font-medium tracking-wide">
                Share your picks. Let others remix.
              </span>
            </div>
          )}
          {/* Footer - visible in read-only mode and exported image */}
          <div 
            data-show-on-export="true"
            className={`mt-3 pt-2.5 border-t border-white/10 flex items-center justify-center ${isReadOnly ? "" : "hidden"}`}
          >
            <span className="text-sm text-muted-foreground/80 font-medium tracking-wide">
              Make yours at {isGeneric ? "pickthree.me" : "pickthree.ai"}
            </span>
          </div>
        </div>
      </div>

      {/* Model Picker Modal - Only shown in AI mode */}
      {!isGeneric && (
        <ModelPicker
          isOpen={activeSlot !== null}
          onClose={handlePickerClose}
          onSelect={handleModelSelect}
          currentSelection={activeSlot !== null ? (rankings[activeSlot - 1]?.model as LLMModel | null) : null}
        />
      )}
    </>
  );
});

